import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { 
  User, 
  Mail, 
  MapPin, 
  Shield, 
  Calendar, 
  Activity,
  Edit3,
  Save,
  X,
  Eye,
  EyeOff
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUserProfile, useUserActivities, useCategories } from '../../hooks';
import { userService, authService } from '../../services/api';
import { Card, Button, Badge, LoadingSpinner, Alert } from '../../components/common';

import toast from 'react-hot-toast';

const ProfilePage: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { data: profileData, refetch } = useUserProfile();
  const { data: activitiesData } = useUserActivities({ limit: 5 });
  const { data: categoriesData } = useCategories();
  
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  
  const categories = categoriesData?.categories || [];

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
    reset: resetProfile
  } = useForm({
    defaultValues: {
      name: user?.name || '',
      email: user?.email || '',
      geographicZone: user?.geographicZone || ''
    }
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    reset: resetPassword,
    watch
  } = useForm();

  const newPassword = watch('newPassword');

  const getCategoryName = (categoryId: string) => {
    const category = categories.find(cat => cat.id === categoryId);
    return category?.name || 'Categoría desconocida';
  };

  const onUpdateProfile = async (data: any) => {
    try {
      setIsLoading(true);
      const response = await userService.updateProfile(data);
      
      if (response.data) {
        updateUser(response.data);
      }
      setIsEditingProfile(false);
      toast.success('Perfil actualizado exitosamente');
      refetch();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error actualizando perfil');
    } finally {
      setIsLoading(false);
    }
  };

  const onChangePassword = async (data: any) => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('authToken');
      
      await authService.changePassword({
        token: token!,
        currentPassword: data.currentPassword,
        newPassword: data.newPassword
      });

      resetPassword();
      setIsChangingPassword(false);
      toast.success('Contraseña actualizada exitosamente');
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Error cambiando contraseña');
    } finally {
      setIsLoading(false);
    }
  };

  const cancelEdit = () => {
    setIsEditingProfile(false);
    resetProfile({
      name: user?.name || '',
      email: user?.email || '',
      geographicZone: user?.geographicZone || ''
    });
  };

  const recentActivities = activitiesData?.users || [];

  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-brinsa-azul mb-4">Mi Perfil</h1>
          <p className="text-lg text-brinsa-azulOscuro/80">
            Gestiona tu información personal y configuración
          </p>
        </div>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Información del perfil */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="lg:col-span-2"
        >
          <Card title="Información Personal">
            <form onSubmit={handleProfileSubmit(onUpdateProfile)}>
              <div className="space-y-6">
                {/* Avatar y nombre */}
                <div className="flex items-center space-x-6">
                  <div className="w-20 h-20 bg-brinsa-azul rounded-full flex items-center justify-center">
                    <span className="text-2xl font-bold text-white">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-brinsa-azul">
                      {user?.name}
                    </h3>
                    <p className="text-brinsa-azulOscuro/80">{user?.cedula}</p>
                    <Badge variant={user?.role === 'admin' ? 'info' : 'default'} size="sm">
                      {user?.role === 'admin' ? 'Administrador' : 'Usuario'}
                    </Badge>
                  </div>
                </div>

                {/* Campos del formulario */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-brinsa-azul mb-2">
                      Nombre completo
                    </label>
                    <div className="relative">
                      <User className="absolute left-3 top-3 h-4 w-4 text-brinsa-celeste" />
                      <input
                        {...registerProfile('name', {
                          required: 'El nombre es requerido',
                          minLength: {
                            value: 2,
                            message: 'El nombre debe tener al menos 2 caracteres'
                          }
                        })}
                        type="text"
                        disabled={!isEditingProfile}
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-celeste focus:border-brinsa-celeste disabled:bg-slate-50 disabled:text-slate-500"
                      />
                    </div>
                    {profileErrors.name && (
                      <p className="mt-1 text-sm text-brinsa-rojo">{profileErrors.name.message}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brinsa-azul mb-2">
                      Email
                    </label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-3 h-4 w-4 text-brinsa-celeste" />
                      <input
                        {...registerProfile('email', {
                          pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: 'Email inválido'
                          }
                        })}
                        type="email"
                        disabled={!isEditingProfile}
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-celeste focus:border-brinsa-celeste disabled:bg-slate-50 disabled:text-slate-500"
                        placeholder="usuario@ejemplo.com"
                      />
                    </div>
                    {profileErrors.email && (
                      <p className="mt-1 text-sm text-brinsa-rojo">{profileErrors.email.message}</p>
                    )}
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-brinsa-azul mb-2">
                      Zona geográfica
                    </label>
                    <div className="relative">
                      <MapPin className="absolute left-3 top-3 h-4 w-4 text-brinsa-celeste" />
                      <input
                        {...registerProfile('geographicZone')}
                        type="text"
                        disabled={!isEditingProfile}
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-celeste focus:border-brinsa-celeste disabled:bg-slate-50 disabled:text-slate-500"
                        placeholder="Ej: Bogotá, Medellín, Cali..."
                      />
                    </div>
                  </div>
                </div>

                {/* Botones de acción */}
                <div className="flex justify-end space-x-3">
                  {isEditingProfile ? (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={cancelEdit}
                        disabled={isLoading}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        isLoading={isLoading}
                        leftIcon={<Save className="w-4 h-4" />}
                      >
                        Guardar cambios
                      </Button>
                    </>
                  ) : (
                    <Button
                      type="button"
                      onClick={() => setIsEditingProfile(true)}
                      leftIcon={<Edit3 className="w-4 h-4" />}
                    >
                      Editar perfil
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </Card>

          {/* Cambiar contraseña */}
          <Card title="Seguridad" className="mt-6">
            {!isChangingPassword ? (
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="font-medium text-brinsa-azul">Contraseña</h4>
                  <p className="text-sm text-brinsa-azulOscuro/80">
                    Última actualización: hace tiempo
                  </p>
                </div>
                <Button
                  className='m-4'
                  variant="outline"
                  onClick={() => setIsChangingPassword(true)}
                  leftIcon={<Shield className="w-4 h-4" />}
                >
                  Cambiar contraseña
                </Button>
              </div>
            ) : (
              <form onSubmit={handlePasswordSubmit(onChangePassword)}>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-brinsa-azul mb-2">
                      Contraseña actual
                    </label>
                    <div className="relative">
                      <input
                        {...registerPassword('currentPassword', {
                          required: 'La contraseña actual es requerida'
                        })}
                        type={showCurrentPassword ? 'text' : 'password'}
                        className="w-full pr-10 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-celeste focus:border-brinsa-celeste"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      >
                        {showCurrentPassword ? (
                          <EyeOff className="h-4 w-4 text-slate-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-slate-400" />
                        )}
                      </button>
                    </div>
                    {passwordErrors.currentPassword && (
                      <p className="mt-1 text-sm text-brinsa-rojo">
                        {String(passwordErrors.currentPassword.message)}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brinsa-azul mb-2">
                      Nueva contraseña
                    </label>
                    <div className="relative">
                      <input
                        {...registerPassword('newPassword', {
                          required: 'La nueva contraseña es requerida',
                          minLength: {
                            value: 6,
                            message: 'La contraseña debe tener al menos 6 caracteres'
                          }
                        })}
                        type={showNewPassword ? 'text' : 'password'}
                        className="w-full pr-10 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-celeste focus:border-brinsa-celeste"
                      />
                      <button
                        type="button"
                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                      >
                        {showNewPassword ? (
                          <EyeOff className="h-4 w-4 text-slate-400" />
                        ) : (
                          <Eye className="h-4 w-4 text-slate-400" />
                        )}
                      </button>
                    </div>
                    {passwordErrors.newPassword && (
                      <p className="mt-1 text-sm text-brinsa-rojo">
                        {String(passwordErrors.newPassword.message)}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-brinsa-azul mb-2">
                      Confirmar nueva contraseña
                    </label>
                    <input
                      {...registerPassword('confirmPassword', {
                        required: 'Confirma tu nueva contraseña',
                        validate: (value) =>
                          value === newPassword || 'Las contraseñas no coinciden'
                      })}
                      type="password"
                      className="w-full py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-brinsa-celeste focus:border-brinsa-celeste"
                    />
                    {passwordErrors.confirmPassword && (
                      <p className="mt-1 text-sm text-brinsa-rojo">
                        {String(passwordErrors.confirmPassword.message)}
                      </p>
                    )}
                  </div>

                  <div className="flex justify-end space-x-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setIsChangingPassword(false);
                        resetPassword();
                      }}
                      disabled={isLoading}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      isLoading={isLoading}
                    >
                      Actualizar contraseña
                    </Button>
                  </div>
                </div>
              </form>
            )}
          </Card>
        </motion.div>

        {/* Panel lateral */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="space-y-6"
        >
          {/* Información de cuenta */}
          <Card title="Información de cuenta">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-sm text-brinsa-azulOscuro/80">Estado:</span>
                <Badge variant="success" size="sm">
                  Activo
                </Badge>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-brinsa-azulOscuro/80">Registro:</span>
                <span className="text-sm text-brinsa-azul">
                  {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('es-ES') : '-'}
                </span>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-brinsa-azulOscuro/80">Último acceso:</span>
                <span className="text-sm text-brinsa-azul">
                  {user?.lastLogin ? new Date(user.lastLogin).toLocaleDateString('es-ES') : 'Primer acceso'}
                </span>
              </div>
            </div>
          </Card>

          {/* Actividad reciente */}
          <Card title="Actividad reciente">
            {recentActivities.length > 0 ? (
              <div className="space-y-3">
                {recentActivities.map((activity) => (
                  <div key={activity.id} className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-brinsa-celeste/15 rounded-full flex items-center justify-center">
                      <Activity className="w-4 h-4 text-brinsa-celeste" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-brinsa-azul truncate">
                        {getCategoryName(activity.categoryId)}
                      </p>
                      <p className="text-xs text-brinsa-azulOscuro/70">
                        {activity.activityType} - {activity.completed ? 'Completado' : 'En progreso'}
                      </p>
                    </div>
                    <Badge 
                      variant={activity.completed ? 'success' : 'warning'}
                      size="sm"
                    >
                      {activity.completed ? 'OK' : '...'}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <Activity className="w-8 h-8 text-brinsa-azulOscuro/50 mx-auto mb-2" />
                <p className="text-sm text-brinsa-azulOscuro/70">Sin actividad reciente</p>
              </div>
            )}
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default ProfilePage;
